// ─── "What does this order need?" — one dot per order ───────────────────────
// Every state below is derived from a field that ALREADY exists in the system.
// Nothing new is invented: statuses come from ORDER_STATUS_LABELS, the follow-up
// states from followUpStateMachine's deriveSubState, confirmation from
// agent_confirmations, review from client_review_status, print health from
// pdf_status.
//
// Design rules (agreed with Yahya):
//   • ONE dot per order — the highest-priority need wins, so a row is never a
//     row of competing colours.
//   • NO dot when nothing is needed. Absence is the calm default; a screen full
//     of green ticks is noise, and colour fatigue is what makes operators stop
//     reading indicators at all.
//   • red = blocked, someone must intervene before this can move
//     amber = needs a person's action now
//     blue  = waiting on money before it may print
//     grey  = waiting on the client, already nudged
import { paidState, paidAtLeast } from './paidState';
import type { Order } from './api';

export type ActionLevel = 'blocked' | 'action' | 'waiting-payment' | 'nudged';

export interface OrderActionState {
  level: ActionLevel;
  /** Dot colour. */
  color: string;
  /** Plain-French explanation for the title/tooltip. */
  label: string;
}

// Night mode (2026-10-02): tokens, so the dot follows the admin theme. Light
// values are unchanged (#B91C1C, #B45309, #1D4ED8, #9CA3AF). Blue has no
// contract token: the dot's own element sets --adm-dot-blue in dark
// (dark:[--adm-dot-blue:#93C5FD]); anywhere else it falls back to the light blue.
const RED = 'var(--color-status-stuck)';
const AMBER = 'var(--color-status-waiting)';
const BLUE = 'var(--adm-dot-blue, #1D4ED8)';
const GREY = 'var(--color-adm-faint)';

/** Statuses that mean an agent still owes this order a confirmation click. */
const AWAITING_CONFIRMATION = ['album-submitted'];

/** Stages where an order belongs to the shared queue and should have an owner.
 *  Mirrors QUEUE_STATUSES in the worker (worker/src/lib/orderAssignment.ts) —
 *  keep the two lists in step. */
const QUEUE_STATUSES = ['pending-album', 'album-submitted', 'paid'];

/** AMEEX canonical statuses that mean the parcel will not arrive as-is.
 *  Taken verbatim from AmeexCanonicalStatus in api.ts — there is no 'failed'
 *  status in this system, so do not add one. 'refunded' is a resolved outcome
 *  and 'postponed' is a soft delay, so neither raises a blocker. */
const AMEEX_TROUBLE = ['refused', 'returned', 'cancelled', 'unresolved'];

export interface ActionStateInput {
  order: Pick<
    Order,
    'status' | 'pdf_status' | 'ameex_status' | 'paid_in_full' | 'paid_at'
  > & Partial<Pick<Order, 'advance_amount' | 'advance_received_amount' | 'advance_proof_url' | 'total_price'>> & {
    agent_confirmation?: { confirmed_at: string } | null;
    client_review_status?: string | null;
    /** `null` = in the shared queue. `undefined` = the caller did not ask for
     *  the column, so ownership is unknown and must not be guessed. */
    assigned_agent_id?: string | null;
  };
  /** Sub-state from deriveSubState, when the caller has the follow-up feed. */
  subState?: string | null;
  /** ISO timestamp of the last WhatsApp follow-up, if any. */
  lastFollowUpAt?: string | null;
}

/** Sub-states that are actionable via a WhatsApp follow-up (mirrors
 *  SUB_STATES_SENDABLE in followUpMessages.ts). */
const SENDABLE_SUB_STATES = new Set([
  'cover_missing', 'photos_missing', 'album_2_missing', 'mode_not_chosen',
  'editor_not_opened', 'editor_stuck', 'cover_now_available', 'submitted',
]);

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * The single most urgent thing this order needs, or null if nothing.
 * Priority order is the function's control flow — first match wins.
 */
export function orderActionState(input: ActionStateInput): OrderActionState | null {
  const { order, subState, lastFollowUpAt } = input;
  const status = order.status;

  // Terminal / off-pipeline: nothing to chase.
  if (status === 'delivered' || status === 'cancelled') return null;

  // 1. Print blocked — the PDF could not be produced, so production is stuck.
  if (order.pdf_status === 'failed') {
    return { level: 'blocked', color: RED, label: 'PDF en échec — production bloquée' };
  }

  // 2. Delivery problem — the parcel will not arrive without intervention.
  if (order.ameex_status && AMEEX_TROUBLE.includes(order.ameex_status)) {
    return { level: 'blocked', color: RED, label: `Livraison à traiter (${order.ameex_status})` };
  }

  // 3. Nobody has taken it (2026-08-11). Until an order has an owner, no one is
  //    chasing it — that outranks any specific task on it. Strictly `=== null`:
  //    a caller whose query did not select the column sends `undefined`, and
  //    guessing "unclaimed" there would paint the whole list amber.
  if (order.assigned_agent_id === null && QUEUE_STATUSES.includes(status)) {
    return { level: 'action', color: AMBER, label: 'À prendre en charge' };
  }

  // 4. Needs the agent's confirmation click (drives the commission record too).
  if (AWAITING_CONFIRMATION.includes(status) && !order.agent_confirmation) {
    return { level: 'action', color: AMBER, label: 'À confirmer' };
  }

  // 4. The client reviewed the flipbook and asked for changes.
  if (order.client_review_status === 'changes_requested') {
    return { level: 'action', color: AMBER, label: 'Le client demande des modifications' };
  }

  // 5. Stalled in the funnel and reachable by WhatsApp. Already nudged inside
  //    24h drops to grey rather than nagging the operator to nudge again.
  if (subState && SENDABLE_SUB_STATES.has(subState)) {
    const nudgedRecently =
      !!lastFollowUpAt && Date.now() - Date.parse(lastFollowUpAt) < DAY_MS;
    return nudgedRecently
      ? { level: 'nudged', color: GREY, label: 'Relancé — en attente du client' }
      : { level: 'action', color: AMBER, label: 'Relance à envoyer' };
  }

  // 6. Ready to print but unpaid — the server refuses the "printed" transition
  //    until it is paid, so flag it before someone tries.
  // « Paid » is lib/paidState.ts's answer (2026-10-06): the AMEEX card's
  // acompte (#WA-168784) or an accepted receipt is money in, not only
  // paid_in_full / paid_at.
  if (status === 'ready-to-print' && !paidAtLeast(paidState(order).level, 'advance')) {
    return { level: 'waiting-payment', color: BLUE, label: 'Paiement requis avant impression' };
  }

  return null;
}
